import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { X, Printer, Search, SlidersHorizontal, Type, Check, Minus, Plus, FileDown, Image } from 'lucide-react';
import { CoordinatorAvatar } from './CoordinatorAvatar';
import {
  buildHierarchyModel,
  createHierarchyFilterSuggestionsFetcher,
} from '../utils/equipe2HierarchyHelpers';
import { exportHierarchyPdf, exportHierarchyPng } from '../utils/equipe2HierarchyExport';
import { Equipe2TechFilters } from './Equipe2TechFilters';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../experiencia-cliente/components/ExperienciaClientePage.css';
import '../../experiencia-cliente/components/ExperienciaClienteDetalhesModal.css';
import './Equipe2HierarchyModal.css';

const HIERARCHY_ZOOM_MIN = 0.5;
const HIERARCHY_ZOOM_MAX = 1.5;
const HIERARCHY_ZOOM_STEP = 0.1;
const HIERARCHY_ZOOM_DEFAULT = 1;

function HierarchyTechAvatar({ tech, showName }) {
  return (
    <div
      className={[
        'eq2-hierarchy-tech-avatar',
        'board-clickup-icon-btn-anchor',
        showName ? 'is-named' : '',
        tech.matched ? 'is-matched' : '',
        tech.dim ? 'is-dimmed' : '',
      ].filter(Boolean).join(' ')}
      style={{
        '--hc-soft': tech.colorSoft,
        '--hc-strong': tech.colorStrong,
      }}
      title={showName ? tech.name : undefined}
    >
      {showName ? (
        <span className="eq2-hierarchy-tech-name">{tech.name}</span>
      ) : (
        tech.initials
      )}
      {!showName ? (
        <span className="board-clickup-tooltip board-clickup-tooltip--top" role="tooltip">
          {tech.name}
        </span>
      ) : null}
    </div>
  );
}

function HierarchyAreaCard({ area, showTechNames }) {
  return (
    <div
      className={[
        'eq2-hierarchy-area-card',
        area.areaMatched ? 'is-highlighted' : '',
        area.areaDim ? 'is-dimmed' : '',
      ].filter(Boolean).join(' ')}
      style={{
        '--hc-soft': area.colorSoft,
        '--hc-strong': area.colorStrong,
      }}
    >
      <div className="eq2-hierarchy-area-head">
        <span className="eq2-hierarchy-area-abbr">{area.abbr}</span>
        <span className="eq2-hierarchy-area-name">{area.name}</span>
        <span className="eq2-hierarchy-area-count">{area.count}</span>
      </div>
      <div className="eq2-hierarchy-area-techs">
        {area.technicians.map((tech) => (
          <HierarchyTechAvatar key={tech.id} tech={tech} showName={showTechNames} />
        ))}
        {area.hasNoTechs && (
          <span className="eq2-hierarchy-no-techs">Sem técnicos</span>
        )}
      </div>
    </div>
  );
}

function HierarchyCoordColumn({ coord, photoUrl, showTechNames }) {
  return (
    <div
      className={['eq2-hierarchy-column', coord.coordDim ? 'is-dimmed' : ''].filter(Boolean).join(' ')}
      style={{ '--hc-soft': coord.colorSoft, '--hc-strong': coord.colorStrong }}
    >
      <div className="eq2-hierarchy-coord-card">
        <div
          className="eq2-hierarchy-coord-avatar-ring"
          style={{ background: coord.colorStrong }}
        >
          <CoordinatorAvatar
            name={coord.name}
            photoUrl={photoUrl}
            color={{ soft: '#fff', strong: coord.colorStrong }}
            size={60}
          />
        </div>
        <div className="eq2-hierarchy-coord-info">
          <div className="eq2-hierarchy-coord-name">{coord.name}</div>
          <div className="eq2-hierarchy-coord-role">Coordenador</div>
        </div>
        <div className="eq2-hierarchy-coord-pills">
          <span className="eq2-hierarchy-pill eq2-hierarchy-pill--accent">
            {coord.areaCount} áreas
          </span>
          <span className="eq2-hierarchy-pill">
            {coord.techCount} técnicos
          </span>
        </div>
      </div>
      <div className="eq2-hierarchy-connector" />
      <div className="eq2-hierarchy-tree">
        {coord.areas.map((area) => (
          <HierarchyAreaCard key={area.id} area={area} showTechNames={showTechNames} />
        ))}
      </div>
    </div>
  );
}

function HierarchyUnassignedColumn({ areas, showTechNames }) {
  return (
    <div className="eq2-hierarchy-column eq2-hierarchy-column--unassigned">
      <div className="eq2-hierarchy-coord-card eq2-hierarchy-coord-card--unassigned">
        <div className="eq2-hierarchy-unassigned-icon">?</div>
        <div className="eq2-hierarchy-coord-info">
          <div className="eq2-hierarchy-coord-name eq2-hierarchy-coord-name--muted">Sem coordenador</div>
          <div className="eq2-hierarchy-coord-role">Áreas pendentes</div>
        </div>
      </div>
      <div className="eq2-hierarchy-connector eq2-hierarchy-connector--muted" />
      <div className="eq2-hierarchy-tree eq2-hierarchy-tree--dashed">
        {areas.map((area) => (
          <HierarchyAreaCard key={area.id} area={area} showTechNames={showTechNames} />
        ))}
      </div>
    </div>
  );
}

function Equipe2HierarchyOptionsMenu({
  showTechNames,
  onToggleShowTechNames,
  onPrint,
  onExportPdf,
  onExportPng,
  exporting,
}) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  const handleToggleNames = () => {
    onToggleShowTechNames?.();
    setOpen(false);
  };

  const handlePrint = () => {
    setOpen(false);
    onPrint?.();
  };

  const runExport = async (fn) => {
    if (exporting || !fn) return;
    setOpen(false);
    try {
      await fn();
    } catch (e) {
      console.error('[Equipe2HierarchyOptionsMenu] export', e);
    }
  };

  return (
    <div className="ec-actions-menu-wrap eq2-hierarchy-options-wrap" ref={wrapRef}>
      <button
        type="button"
        className={['board-clickup-filter-chip', open ? 'is-open' : ''].filter(Boolean).join(' ')}
        aria-label="Opções da estrutura da equipe"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={exporting}
      >
        <span className="board-clickup-filter-chip-icon" aria-hidden="true">
          <SlidersHorizontal size={13} strokeWidth={1.75} />
        </span>
        <span className="board-clickup-filter-chip-text">
          <span className="board-clickup-filter-chip-label">Opções</span>
        </span>
      </button>
      {open ? (
        <div className="ec-actions-menu" role="menu" aria-label="Opções da estrutura da equipe">
          <button
            type="button"
            className={['ec-actions-menu-item', showTechNames ? 'is-active' : ''].filter(Boolean).join(' ')}
            role="menuitemcheckbox"
            aria-checked={showTechNames}
            onClick={handleToggleNames}
            disabled={exporting}
          >
            <Type size={16} strokeWidth={1.5} aria-hidden />
            Nomear técnicos
            {showTechNames ? (
              <Check size={14} strokeWidth={2.2} className="eq2-hierarchy-options-check" aria-hidden />
            ) : null}
          </button>
          <button
            type="button"
            className="ec-actions-menu-item"
            role="menuitem"
            onClick={handlePrint}
            disabled={exporting}
          >
            <Printer size={16} strokeWidth={1.5} aria-hidden />
            Imprimir
          </button>
          <button
            type="button"
            className="ec-actions-menu-item"
            role="menuitem"
            onClick={() => runExport(onExportPdf)}
            disabled={exporting}
          >
            <FileDown size={16} strokeWidth={1.5} aria-hidden />
            Exportar PDF
          </button>
          <button
            type="button"
            className="ec-actions-menu-item"
            role="menuitem"
            onClick={() => runExport(onExportPng)}
            disabled={exporting}
          >
            <Image size={16} strokeWidth={1.5} aria-hidden />
            Exportar PNG
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Equipe2HierarchyModal({
  coordenadores,
  areas,
  tecnicos,
  coordinatorPhotos = {},
  techFilters,
  onTechFiltersChange,
  filterContext,
  gestaoByCodigo,
  espLinks = [],
  onClose,
}) {
  const [search, setSearch] = useState('');
  const [showTechNames, setShowTechNames] = useState(false);
  const [zoom, setZoom] = useState(HIERARCHY_ZOOM_DEFAULT);
  const [isPanning, setIsPanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const bodyRef = useRef(null);
  const boardRef = useRef(null);
  const panRef = useRef(null);
  const [boardSize, setBoardSize] = useState({ width: 0, height: 0 });

  const fetchFilterSuggestions = useMemo(
    () => createHierarchyFilterSuggestionsFetcher({ coordenadores, areas, tecnicos, gestaoByCodigo }),
    [coordenadores, areas, tecnicos, gestaoByCodigo],
  );

  const model = useMemo(
    () => buildHierarchyModel({
      coordenadores,
      areas,
      tecnicos,
      search,
      filters: techFilters,
      gestaoByCodigo,
      espLinks,
    }),
    [coordenadores, areas, tecnicos, search, techFilters, gestaoByCodigo, espLinks],
  );

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      await exportHierarchyPdf(bodyRef.current);
    } finally {
      setExporting(false);
    }
  }, []);

  const handleExportPng = useCallback(async () => {
    setExporting(true);
    try {
      await exportHierarchyPng(bodyRef.current);
    } finally {
      setExporting(false);
    }
  }, []);

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose?.();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const update = () => {
      setBoardSize({ width: el.offsetWidth, height: el.offsetHeight });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [model, showTechNames]);

  useEffect(() => {
    if (!isPanning) return undefined;
    const onMove = (e) => {
      const body = bodyRef.current;
      const start = panRef.current;
      if (!body || !start) return;
      body.scrollLeft = start.scrollLeft - (e.clientX - start.x);
      body.scrollTop = start.scrollTop - (e.clientY - start.y);
    };
    const onUp = () => {
      setIsPanning(false);
      panRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isPanning]);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(HIERARCHY_ZOOM_MIN, Math.round((z - HIERARCHY_ZOOM_STEP) * 10) / 10));
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(HIERARCHY_ZOOM_MAX, Math.round((z + HIERARCHY_ZOOM_STEP) * 10) / 10));
  }, []);

  const handlePanStart = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.closest('.eq2-hierarchy-zoom-bar, button, a, input, textarea, select')) return;
    const body = bodyRef.current;
    if (!body) return;
    setIsPanning(true);
    panRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: body.scrollLeft,
      scrollTop: body.scrollTop,
    };
    e.preventDefault();
  }, []);

  return (
    <div
      className="eq2-hierarchy-overlay equipe2-hierarchy-print"
      role="dialog"
      aria-modal="true"
      aria-label="Estrutura da equipe"
      onClick={handleBackdrop}
    >
      <div className="eq2-hierarchy-modal">
        <header className="eq2-hierarchy-header no-print">
          <div className="eq2-hierarchy-header-left">
            <h2 className="eq2-hierarchy-title">Estrutura da equipe</h2>
            <div className="eq2-hierarchy-stats">
              <span className="eq2-hierarchy-stat eq2-hierarchy-stat--primary">
                {model.stats.coordCount} coordenadores
              </span>
              <span className="eq2-hierarchy-stat">
                {model.stats.areaCount} áreas
              </span>
              <span className="eq2-hierarchy-stat">
                {model.stats.techCount} técnicos
              </span>
            </div>
          </div>
          <div className="eq2-hierarchy-header-actions">
            <div className="board-toolbar-expand-search is-expanded eq2-hierarchy-expand-search">
              <span className="board-toolbar-expand-search-icon" aria-hidden="true">
                <Search size={13} strokeWidth={1.75} />
              </span>
              <input
                type="search"
                className="board-toolbar-expand-search-input"
                placeholder="Buscar coordenador, área ou técnico..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Buscar na estrutura"
              />
            </div>
            <Equipe2TechFilters
              filters={techFilters}
              onChange={onTechFiltersChange}
              onFetchSuggestions={fetchFilterSuggestions}
              filterContext={filterContext}
            />
            <Equipe2HierarchyOptionsMenu
              showTechNames={showTechNames}
              onToggleShowTechNames={() => setShowTechNames((v) => !v)}
              onPrint={handlePrint}
              onExportPdf={handleExportPdf}
              onExportPng={handleExportPng}
              exporting={exporting}
            />
            <button
              type="button"
              className="eq2-hierarchy-close"
              onClick={onClose}
              aria-label="Fechar"
            >
              <X size={16} strokeWidth={2.2} aria-hidden />
            </button>
          </div>
        </header>

        <div className="eq2-hierarchy-canvas-wrap">
          <div className="eq2-hierarchy-zoom-bar no-print" role="group" aria-label="Zoom da estrutura">
            <button
              type="button"
              className="eq2-hierarchy-zoom-btn"
              onClick={handleZoomOut}
              disabled={zoom <= HIERARCHY_ZOOM_MIN}
              aria-label="Diminuir zoom"
            >
              <Minus size={14} strokeWidth={2.2} aria-hidden />
            </button>
            <span className="eq2-hierarchy-zoom-label">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              className="eq2-hierarchy-zoom-btn"
              onClick={handleZoomIn}
              disabled={zoom >= HIERARCHY_ZOOM_MAX}
              aria-label="Aumentar zoom"
            >
              <Plus size={14} strokeWidth={2.2} aria-hidden />
            </button>
          </div>
          <div
            ref={bodyRef}
            className={[
              'eq2-hierarchy-body',
              'custom-scroll',
              isPanning ? 'is-panning' : 'is-pannable',
            ].filter(Boolean).join(' ')}
            onMouseDown={handlePanStart}
          >
            <div
              className="eq2-hierarchy-scaler"
              style={{
                width: boardSize.width ? boardSize.width * zoom : undefined,
                height: boardSize.height ? boardSize.height * zoom : undefined,
              }}
            >
              <div
                ref={boardRef}
                className="eq2-hierarchy-board"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                }}
              >
                {model.coordinators.map((coord) => (
                  <HierarchyCoordColumn
                    key={coord.id}
                    coord={coord}
                    photoUrl={coordinatorPhotos[coord.id]}
                    showTechNames={showTechNames}
                  />
                ))}
                {model.hasUnassigned && (
                  <HierarchyUnassignedColumn
                    areas={model.unassignedAreas}
                    showTechNames={showTechNames}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Equipe2HierarchyModal;
